import {
  IOffboardingAuditCategory,
  IOffboardingListStatusTab,
  IOffboardingReviewAccessSection,
  IOffboardingReviewAccessSourceTag,
  IOffboardingStatusPill,
  OffboardingReviewAccessSource,
} from '../interfaces/offboarding.interface';
import { TOOL_ACCESS_CATEGORY_NAMES } from './tool-access.constants';

export const OFFBOARDING_EMPLOYEE_SEARCH_EMPTY_TITLE = 'No employee selected';
export const OFFBOARDING_EMPLOYEE_SEARCH_EMPTY_SUBTITLE =
  'Search and select an employee to begin offboarding.';
export const OFFBOARDING_PENDING_TOOLS_EMPTY_TITLE = 'No pending tools';
export const OFFBOARDING_PENDING_TOOLS_EMPTY_SUBTITLE = 'All tool access has been revoked, declined or resolved for this employee.';
export const OFFBOARDING_COMPLETED_TOOLS_EMPTY_TITLE = 'No completed tools yet';
export const OFFBOARDING_COMPLETED_TOOLS_EMPTY_SUBTITLE = 'Tools that are revoked, declined or resolved for this employee will be shown here.';
export const OFFBOARDING_EMPLOYEE_SEARCH_EMPTY_IMAGE = 'assets/58x58/no_tickets.svg';
export const OFFBOARDING_EMPLOYEE_SEARCH_DEBOUNCE_MS = 400;
export const OFFBOARDING_EMPLOYEE_SEARCH_MIN_CHARS = 2;
export const OFFBOARDING_EMPLOYEE_SEARCH_MAX_RESULTS = 50;
export const OFFBOARDING_EMPLOYEE_SEARCH_PLACEHOLDER = 'Search by name, email, or Employee ID';

export const OFFBOARDING_EMPLOYEE_ROUTE_PARAM = 'id';
export const OFFBOARDING_RETURN_TO_STATE_KEY = 'returnTo';
export const OFFBOARDING_SELECTED_EMPLOYEE_ID_STORAGE_KEY = 'offboarding.selectedEmployeeId';
export const OFFBOARDING_SEARCH_TEXT_STORAGE_KEY = 'offboarding.searchText';
export const OFFBOARDING_FINANCE_CONFIRMED_STORAGE_KEY_PREFIX = 'offboarding.financeConfirmed:';
export const OFFBOARDING_EMPLOYEE_NOT_FOUND_MESSAGE = "Couldn't load the selected employee.";

export const OFFBOARDING_PROVIDERS: { slug: string; subCategory: string; handleLabel: string; supportsManualLink: boolean }[] = [
  { slug: 'github', subCategory: 'GitHub', handleLabel: 'GitHub username', supportsManualLink: true },
  { slug: 'proofhub', subCategory: 'ProofHub', handleLabel: 'ProofHub account', supportsManualLink: false },
];

export const OFFBOARDING_RESOURCE_TYPE_LABEL_MAP: Record<string, string> = {
  ORG: 'Organisation',
  REPO: 'Repository',
  CREATE_REPO: 'Repository',
  ACCOUNT: 'Account',
  PROJECT: 'Project',
};

export const OFFBOARDING_TERMINAL_TOOL_ACCESS_STATUSES = ['REVOKED', 'DECLINED', 'EXPIRED'];
export const OFFBOARDING_DECLINABLE_TOOL_ACCESS_STATUSES = ['PENDING'];
export const OFFBOARDING_CHECKLIST_REVOKED_STATUS = 'REVOKED';
export const OFFBOARDING_CHECKLIST_CARD_ID_PREFIX = 'checklist:';
export const OFFBOARDING_RECORD_CARD_ID_PREFIX = 'record:';
export const OFFBOARDING_UNVERIFIED_ACCESS_FALLBACK_WARNING = "Couldn't verify this tool's live access. Please try again.";

export const OFFBOARDING_STATUS_PILL = {
  ACTIVE: { label: 'Active', className: 'b-pending' },
  MANUAL_REMOVAL_REQUIRED: { label: 'Manual Removal Required', className: 'b-info' },
  REVOKING: { label: 'Revoking…', className: 'b-revoking' },
  FAILED: { label: 'Failed', className: 'b-failed' },
  REMOVED_EXTERNALLY: { label: 'Already removed externally', className: 'b-warning' },
  ACCOUNT_NOT_LINKED: { label: 'Account not linked', className: 'b-pending' },
  NO_ACCOUNT: { label: 'No account', className: 'b-revoked' },
  NO_ACCESS_FOUND: { label: 'No access found', className: 'b-revoked' },
  REQUEST_PENDING: { label: 'Request Pending', className: 'b-pending' },
  REVOKED: { label: 'Revoked', className: 'b-active' },
  ALREADY_REMOVED: { label: 'Already Removed', className: 'b-revoked' },
  DECLINED: { label: 'Declined', className: 'b-revoked' },
  EXPIRED: { label: 'Expired', className: 'b-revoked' },
  PENDING_CHECK_IN: { label: 'Pending Check-in', className: 'b-pending' },
} satisfies Record<string, IOffboardingStatusPill>;

export const OFFBOARDING_REVOKE_REASON = 'Offboarding';
export const OFFBOARDING_CHECKLIST_ACCESS_TYPES: Record<string, string> = {
  'Network / Security': TOOL_ACCESS_CATEGORY_NAMES.NETWORK_AND_INFRASTRUCTURE,
};

export const OFFBOARDING_GRANTED_OUTSIDE_STESSA_HINT = 'Granted outside Stessa';
export const OFFBOARDING_REVOKE_FAILED_MESSAGE = 'Revocation failed. Try again.';
export const OFFBOARDING_MANUAL_REMOVAL_INFO =
  "API verification isn't available for this tool, so access has to be revoked manually in the external system. Steps: remove it outside Stessa → Mark as Revoked.";
export const OFFBOARDING_GITHUB_LINK_PERMANENT_LABEL = 'Linking is permanent';
export const OFFBOARDING_GITHUB_LINK_PERMANENT_NOTE =
  'Once linked, this GitHub account is permanently associated with this employee and cannot be changed from Stessa. Please verify the username before continuing.';

export const OFFBOARDING_REVOKABLE_LIVE_STATUSES = ['ACTIVE', 'ACTIVE_NOT_TRACKED'];

export const OFFBOARDING_TOOL_METHOD_LABEL = {
  automatic: 'Automatic (API)',
  manual: 'Manual removal',
} as const;

export const OFFBOARDING_ACTION_FLASH_MS = 1000;
export const OFFBOARDING_FLASH_REVOKED = 'Revoked';
export const OFFBOARDING_FLASH_MARKED_REVOKED = 'Marked as revoked';
export const OFFBOARDING_FLASH_DECLINED = 'Request declined';
export const OFFBOARDING_PROGRESS_CHECKING_IN = 'Checking in…';
export const OFFBOARDING_PROGRESS_REMOVING_ACCESS = 'Removing access…';
export const OFFBOARDING_PROGRESS_MARKING_REVOKED = 'Marking as revoked…';
export const OFFBOARDING_PROGRESS_DECLINING = 'Declining…';
export const OFFBOARDING_PROGRESS_CHECKING_ACCESS = 'Checking access…';
export const OFFBOARDING_PROGRESS_CONFIRMING = 'Confirming…';

export const OFFBOARDING_COMPLETE_CONFIRMED = 'COMPLETE';
export const OFFBOARDING_COMPLETED_BADGE = 'Offboarding Completed';
export const OFFBOARDING_GO_TO_COMPLETION_LABEL = 'Go to Completion';
export const OFFBOARDING_COULD_NOT_VERIFY_LABEL = "couldn't verify, retry";

/* ── Review access (design #gh-modal) ── */

export const OFFBOARDING_REVIEW_ACCESS_PROVIDER_SLUGS = ['github', 'proofhub'];
export const OFFBOARDING_REVIEW_ACCESS_SECTIONS: Record<string, IOffboardingReviewAccessSection[]> = {
  github: [
    { title: 'Org Access', columnLabel: 'Organisation', resourceTypes: ['ORG'] },
    { title: 'Repo Access', columnLabel: 'Repository', resourceTypes: ['REPO', 'CREATE_REPO'] },
  ],
  proofhub: [
    { title: 'Account Access', columnLabel: 'Account', resourceTypes: ['ACCOUNT'] },
    { title: 'Project Access', columnLabel: 'Project', resourceTypes: ['PROJECT'] },
  ],
};
export const OFFBOARDING_REVIEW_ACCESS_SOURCE_TAG: Record<string, Record<OffboardingReviewAccessSource, IOffboardingReviewAccessSourceTag>> = {
  github: {
    stessa: { label: 'Stessa', className: 'tag-stessa' },
    external: { label: 'GitHub', className: 'tag-github', title: 'Granted on GitHub outside Stessa' },
    both: { label: 'Stessa + GitHub', className: 'tag-both' },
  },
  proofhub: {
    stessa: { label: 'Stessa', className: 'tag-stessa' },
    external: { label: 'ProofHub', className: 'tag-github', title: 'Granted on ProofHub outside Stessa' },
    both: { label: 'Stessa + ProofHub', className: 'tag-both' },
  },
};
export const OFFBOARDING_REVIEW_ACCESS_LEGEND: Record<string, string> = {
  github: 'Items tagged GitHub were granted outside Stessa.',
  proofhub: 'Items tagged ProofHub were granted outside Stessa.',
};
export const OFFBOARDING_NO_ACCESS_FOUND_SCOPE: Record<string, string> = {
  github: 'the organization or any repository',
  proofhub: 'any ProofHub project',
};
export const OFFBOARDING_REVIEW_ACCESS_LAST_RESOURCE_TYPES = ['ACCOUNT'];
export const OFFBOARDING_PROOFHUB_ACCESS_LEVEL_LABEL = 'Normal User';

/* ── Employee overview sections ── */

export const OFFBOARDING_SECTION_IDS = {
  recoveryNotes: 'offboarding-recovery-notes',
  toolAccess: 'offboarding-tool-access',
  assets: 'offboarding-assets',
  auditTrail: 'offboarding-audit-trail',
} as const;
export const OFFBOARDING_SECTION_LABELS = {
  toolAccess: 'Tool/Access',
  assets: 'Assets',
  recoveryNotes: 'Recovery Notes',
} as const;
export const OFFBOARDING_SECTION_HIGHLIGHT_MS = 1500;

export const OFFBOARDING_ACTION_ALREADY_REMOVED = 'Already Removed';


/* ── Audit trail ── */

export const OFFBOARDING_AUDIT_ALL_CATEGORY = 'ALL';
/** Where the backend puts the count of every entry, beside its per-category counts. */
export const OFFBOARDING_AUDIT_ALL_COUNT_KEY = 'all';
export const OFFBOARDING_AUDIT_CATEGORIES: IOffboardingAuditCategory[] = [
  { value: 'ALL', label: 'All', entityTypes: [] },
  { value: 'TOOL_ACCESS', label: 'Access tools', entityTypes: ['TOOL_ACCESS', 'CHECKLIST_ITEM'] },
  { value: 'ASSET', label: 'Assets', entityTypes: ['ASSET'] },
  { value: 'RECOVERY_NOTE', label: 'Recovery notes', entityTypes: ['RECOVERY_NOTE'] },
  { value: 'OFFBOARDING', label: 'Completed', entityTypes: ['OFFBOARDING'] },
];
export const OFFBOARDING_AUDIT_SEARCH_PLACEHOLDER = 'Search tool or action';
export const OFFBOARDING_AUDIT_EMPTY_MESSAGE = 'No actions logged yet.';
export const OFFBOARDING_AUDIT_FINANCE_CONFIRMED_DETAIL = 'Outstanding amounts communicated to Finance';
export const OFFBOARDING_AUDIT_NO_MATCH_MESSAGE = 'No matching activity found for the current search/filter.';
export const OFFBOARDING_AUDIT_DATE_INPUT_FORMAT = 'yyyy-MM-dd';
export const OFFBOARDING_AUDIT_DATE_RANGE_ERROR = "'From' date can't be after the 'To' date.";
export const OFFBOARDING_AUDIT_FUTURE_DATE_ERROR = "Date can't be in the future.";
export const OFFBOARDING_AUDIT_INCOMPLETE_DATE_ERROR = 'Enter a valid date.';
export const OFFBOARDING_AUDIT_INVALID_DATE_RANGE_MESSAGE = 'Select a valid date range to view activity.';
export const OFFBOARDING_AUDIT_MIN_COMPLETE_YEAR = 1000;
export const OFFBOARDING_AUDIT_DATE_DEBOUNCE_MS = 600;
export const OFFBOARDING_AUDIT_LOAD_ERROR = 'Failed to load the audit trail.';
export const OFFBOARDING_AUDIT_CATEGORY_QUERY_MAP: Record<string, string> = {
  TOOL_ACCESS: 'access',
  ASSET: 'assets',
  RECOVERY_NOTE: 'finance',
  OFFBOARDING: 'system',
};
export const OFFBOARDING_AUDIT_PAGE_QUERY_PARAM = 'page';
export const OFFBOARDING_AUDIT_PAGE_SIZE_STORAGE_KEY = 'pageSize:offboarding-audit-trail';
export const OFFBOARDING_AUDIT_SEARCH_DEBOUNCE_MS = 600;
export const OFFBOARDING_AUDIT_TARGET_SEPARATOR = ' · ';
export const OFFBOARDING_AUDIT_ATTACHMENT_FALLBACK_NAME = 'Attachment';
export const OFFBOARDING_AUDIT_PAGE_SIZE_OPTIONS = [5, 10, 20];
export const OFFBOARDING_AUDIT_DONE_ACTIONS = [
  'Revoked',
  'Marked as Revoked (Manual)',
  'No Account Confirmed',
  'Marked as Checked In',
  'Note Settled',
  'Finance Confirmed',
  'Declined',
  'Already Removed',
  'Completed',
];

/* ── Offboarding employee list ── */

export const OFFBOARDING_LIST_TITLE = 'Offboarding Records';
export const OFFBOARDING_LIST_ALL_STATUS = 'ALL';
export const OFFBOARDING_LIST_STATUS_TABS: IOffboardingListStatusTab[] = [
  { value: 'ALL', label: 'All' },
  { value: 'INITIATED', label: 'In progress' },
  { value: 'COMPLETED', label: 'Offboarded' },
];
export const OFFBOARDING_LIST_STATUS_PILL = {
  INITIATED: { label: 'In progress', className: 'b-pending' },
  COMPLETED: { label: 'Offboarded', className: 'b-active' },
} satisfies Record<string, IOffboardingStatusPill>;
export const OFFBOARDING_LIST_EMPTY_MESSAGE = 'No offboardings started yet.';
export const OFFBOARDING_LIST_NO_MATCH_MESSAGE = 'No employees found for the current search/filter.';
export const OFFBOARDING_LIST_INVALID_DATE_RANGE_MESSAGE = 'Select a valid date range to view employees.';
export const OFFBOARDING_LIST_LOAD_ERROR = 'Failed to load the offboarding employees.';
export const OFFBOARDING_LIST_PAGE_QUERY_PARAM = 'page';
export const OFFBOARDING_LIST_PAGE_SIZE_STORAGE_KEY = 'pageSize:offboarding-employee-list';
export const OFFBOARDING_LIST_FILTERS_STORAGE_KEY = 'offboarding.employeeListFilters';
export const OFFBOARDING_SESSION_STORAGE_KEYS = [
  OFFBOARDING_SELECTED_EMPLOYEE_ID_STORAGE_KEY,
  OFFBOARDING_SEARCH_TEXT_STORAGE_KEY,
  OFFBOARDING_LIST_FILTERS_STORAGE_KEY,
];
export const OFFBOARDING_OFFBOARDED_CHIP = 'Offboarded';
